import { fundFamilySetupCreditFacilityRoutes } from "features/credit-facility/CreditFacilityRoutes";
import { fundFamilySetupEntitiesRoutes } from "features/fund-family/setup/entities/FundFamilySetupEntitiesRoutes";
import { fundFamilySetupFundRoutes } from "features/fund-family/setup/fund/FundFamilySetupFundRoutes";
import { fundFamilySetupFundStructureRoutes } from "features/fund-family/setup/fund-structure/FundFamilySetupFundStructureRoutes";
import { fundFamilySetupNoticeRoutes } from "features/fund-family/setup/notices/FundFamilySetupNoticeRoutes";
import { createInvestorEntityRoutes } from "features/investor-entities/InvestorEntityRoutes";
import { getClosingListLoader } from "gen/endpoints/closing/closing";
import { getCommitmentInformationTableDataRetrieveLoader } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { getFeeInformationListLoader } from "gen/endpoints/fee-information/fee-information";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getInvestorClassRetrieveLoader } from "gen/endpoints/investor-class/investor-class";
import { getInvestorClassInformationListLoader } from "gen/endpoints/investor-class-information/investor-class-information";
import { getWaterfallConfigRetrieveLoader } from "gen/endpoints/waterfall/waterfall";
import i18n from "i18n/config";
import { redirect, RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const fundFamilySetupRoutes: RouteObject = {
  id: "fundFamilySetup",
  path: "setup",
  children: [
    {
      id: "fundFamilySetupIndex",
      index: true,
      loader: () => redirect("fund"),
    },
    {
      id: "fundFamilySetupCommitments",
      path: "commitments",
      children: [
        {
          id: "fundFamilySetupCommitmentsIndex",
          index: true,
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { FundFamilySetupCommitmentsPage } = await import(
              "features/fund-family/setup/commitments/FundFamilySetupCommitmentsPage"
            );
            return { Component: FundFamilySetupCommitmentsPage };
          },
          loader: composeLoaders(
            getCommitmentInformationTableDataRetrieveLoader(),
            getClosingListLoader({ is_draft: true }),
          ),
        },
        {
          id: "fundFamilySetupCommitmentsFeeOverride",
          path: ":commitmentId/fees/:feeId/override",
          lazy: async () => {
            const { CommitmentSetupFeeOverridePage } = await import(
              "features/commitments/setup/CommitmentSetupFeeOverridePage"
            );
            return { Component: CommitmentSetupFeeOverridePage };
          },
        },
        {
          id: "fundFamilySetupCommitmentsCommitment",
          path: ":commitmentId/*",
          lazy: async () => {
            const { CommitmentSetupPage } = await import(
              "features/commitments/setup/CommitmentSetupPage"
            );
            return { Component: CommitmentSetupPage };
          },
          children: [
            {
              id: "fundFamilySetupCommitmentsCommitmentBasic",
              path: "",
              lazy: async () => {
                const { CommitmentSetupTabBasic } = await import(
                  "features/commitments/setup/CommitmentSetupTabBasic"
                );
                return { Component: CommitmentSetupTabBasic };
              },
              handle: {
                title: i18n.t("commitment:edit.basic.title"),
              },
            },
            {
              id: "fundFamilySetupCommitmentsCommitmentLetter",
              path: "letter",
              lazy: async () => {
                const { CommitmentSetupTabLetter } = await import(
                  "features/commitments/setup/CommitmentSetupTabLetter"
                );
                return { Component: CommitmentSetupTabLetter };
              },
              handle: {
                title: i18n.t("commitment:edit.letter.title"),
              },
            },
            {
              id: "fundFamilySetupCommitmentsCommitmentFees",
              path: "fees",
              lazy: async () => {
                const { CommitmentSetupTabFees } = await import(
                  "features/commitments/setup/CommitmentSetupTabFees"
                );
                return { Component: CommitmentSetupTabFees };
              },
              handle: {
                title: i18n.t("commitment:edit.fees.title"),
              },
            },
            {
              id: "fundFamilySetupCommitmentsCommitmentWaterfall",
              lazy: async () => {
                const { CommitmentSetupTabWaterfall } = await import(
                  "features/commitments/setup/CommitmentSetupTabWaterfall"
                );
                return { Component: CommitmentSetupTabWaterfall };
              },
              path: "waterfall",
              handle: {
                title: "Waterfall",
              },
            },
          ],
        },
      ],
    },
    {
      id: "fundFamilySetupClasses",
      path: "classes",
      children: [
        {
          id: "fundFamilySetupClassesIndex",
          index: true,
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { CommitmentClassesPage } = await import(
              "features/commitment-classes/CommitmentClassesPage"
            );
            return { Component: CommitmentClassesPage };
          },
          loader: getInvestorClassInformationListLoader(),
        },
        {
          id: "fundFamilySetupClassesEdit",
          path: ":investorClassId/*",
          lazy: async () => {
            const { CommitmentClassSetupPage } = await import(
              "features/commitment-classes/setup/CommitmentClassSetupPage"
            );
            return { Component: CommitmentClassSetupPage };
          },
          children: [
            {
              id: "basic",
              path: "",
              lazy: async () => {
                const { CommitmentClassTabBasicDetails } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabBasicDetails"
                );
                return { Component: CommitmentClassTabBasicDetails };
              },
              loader: composeLoaders(
                getInvestorClassRetrieveLoader(
                  ({ fundFamilyId, investorClassId }) => ({
                    fundFamilyId: fundFamilyId!,
                    investorClassId: investorClassId!,
                  }),
                ),
                getFundListLoader(),
              ),
              handle: {
                title: i18n.t("common:basicDetails"),
              },
            },
            {
              id: "fundFamilySetupClassesEditFees",
              path: "fees",
              lazy: async () => {
                const { CommitmentClassTabFees } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabFees"
                );
                return { Component: CommitmentClassTabFees };
              },
              loader: composeLoaders(
                getFeeInformationListLoader(({ investorClassId }) => ({
                  investor_class_id: investorClassId,
                })),
                getFundListLoader(),
              ),
              handle: {
                title: i18n.t("common:fees"),
              },
            },
            {
              id: "fundFamilySetupClassesEditWaterfall",
              path: "waterfall",
              lazy: async () => {
                const { CommitmentClassTabWaterfall } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabWaterfall"
                );
                return { Component: CommitmentClassTabWaterfall };
              },
              loader: composeLoaders(
                getWaterfallConfigRetrieveLoader(({ investorClassId }) => ({
                  investor_class_id: investorClassId,
                })),
                getFundListLoader(),
              ),
              handle: {
                title: i18n.t("common:waterfall"),
              },
            },
            {
              id: "fundFamilySetupClassesEditCommitments",
              path: "commitments",
              lazy: async () => {
                const { CommitmentClassTabCommitments } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabCommitments"
                );
                return { Component: CommitmentClassTabCommitments };
              },
              loader: getCommitmentInformationTableDataRetrieveLoader(),
              handle: {
                title: i18n.t("common:commitments"),
              },
            },
          ],
        },
        {
          id: "fundFamilySetupClassesFee",
          path: ":investorClassId/fees/:feeId",
          lazy: async () => {
            const { CommitmentClassFeePage } = await import(
              "features/commitment-classes/setup/CommitmentClassFeePage"
            );
            return { Component: CommitmentClassFeePage };
          },
        },
        {
          id: "fundFamilySetupClassesFeeOverride",
          path: ":investorClassId/fees/:feeId/override",
          lazy: async () => {
            const { CommitmentClassSetupFeeOverridePage } = await import(
              "features/commitment-classes/setup/CommitmentClassSetupFeeOverridePage"
            );
            return { Component: CommitmentClassSetupFeeOverridePage };
          },
        },
      ],
    },
    {
      id: "fundFamilySetupClosing",
      path: "closing",
      handle: {
        showSidebar: true,
      },
      children: [
        {
          id: "fundFamilySetupClosingPage",
          path: "*",
          lazy: async () => {
            const { FundFamilySetupClosingPage } = await import(
              "features/fund-family/setup/closing/FundFamilySetupClosingPage"
            );
            return { Component: FundFamilySetupClosingPage };
          },
          children: [
            {
              id: "fundFamilySetupClosingCommitments",
              path: "",
              lazy: async () => {
                const { ClosingTabCommitments } = await import(
                  "features/closings/tabs/ClosingTabCommitments"
                );
                return { Component: ClosingTabCommitments };
              },
              handle: {
                title: i18n.t("common:commitments"),
              },
            },
          ],
        },
      ],
    },
    fundFamilySetupFundRoutes,
    fundFamilySetupFundStructureRoutes,
    {
      id: "create-entity",
      path: "create-entity",
      lazy: async () => {
        const { EntityCreatePage } = await import(
          "features/entities/EntityCreatePage/EntityCreatePage"
        );
        return { Component: EntityCreatePage };
      },
      handle: {
        title: i18n.t("entity:create.title"),
      },
    },
    fundFamilySetupEntitiesRoutes,
    fundFamilySetupNoticeRoutes,
    fundFamilySetupCreditFacilityRoutes,
    createInvestorEntityRoutes("fundFamilySetup"),
  ],
};
